import { useState, useEffect } from 'react'
import { formatDate } from '@the-platform-group/formatters/dateFormat'
import formats from 'constants/formats'

function useCustomerForm({ onSave, editableCustomer }) {
  const { DATE_FORMAT } = formats
  const [customer, setCustomer] = useState(editableCustomer || {})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formErrors, setFormErrors] = useState([])

  useEffect(() => setCustomer(editableCustomer || {}), [editableCustomer])

  const handleChange = ({ target }) => {
    setCustomer({ ...customer, [target.name]: target.value })
  }

  const handleEmailInboundSubdomainChange = value => {
    setCustomer(prev => ({
      ...prev,
      emailInboundAddress: {
        ...prev.emailInboundAddress,
        subdomain: value,
      },
    }))
  }

  const handleChecked = ({ target }) => {
    setCustomer({ ...customer, [target.name]: target.checked })
  }

  const handleDateChange = ({ target }) => {
    setCustomer({ ...customer, [target.name]: target.value })
  }

  const saveCustomer = async () => {
    setIsSubmitting(true)
    setFormErrors([])
    try {
      const payload = {
        ...customer,
        expiredAt: customer.type === 'trial' ? formatDate(customer.expiredAt, DATE_FORMAT) : null,
      }
      payload.emailInboundAddressAttributes = {
        id: payload.emailInboundAddress?.id,
        subdomain: payload.emailInboundAddress?.subdomain?.trim(),
      }
      delete payload.emailInboundAddress
      await onSave(payload)
    } catch (error) {
      // Surface backend validation errors (client errors such as an invalid or
      // already-taken subdomain) inline in the form so the user can correct the
      // input; bubble anything unexpected to the error boundary.
      // formErrors is always a list of { field?, message } objects. A 400 returns
      // that shape directly; a 422 (customer already exists) returns an empty body,
      // so we build an equivalent field-less entry rather than a bare string.
      const status = error.response?.status
      if (status === 422) {
        setFormErrors([{ message: 'The customer was already created.' }])
        return
      }
      if (status === 400) {
        setFormErrors(error.response.data.errors)
        return
      }

      setCustomer(() => {
        throw error
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    state: { customer, isSubmitting, formErrors },
    handlers: {
      handleChange,
      handleChecked,
      handleDateChange,
      handleEmailInboundSubdomainChange,
      saveCustomer,
    },
  }
}

export default useCustomerForm
